import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";
import {
      type BrandSearchActivity,
      brandActivityFromRealtimePayload,
      type SearchActivityRealtimePayload,
      type SearchExecutionActivity,
      searchActivityFromRealtimePayload,
} from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";

function upsertPaginatedSearchActivity<TItem extends SearchExecutionActivity, TResult extends { items: Array<TItem> }>(
      previous: TResult | undefined,
      activity: SearchExecutionActivity,
): TResult | undefined {
      if (!previous) {
            return previous;
      }

      const existingIndex = previous.items.findIndex((item) => item.id === activity.id);
      const nextItems = [...previous.items];

      if (existingIndex >= 0) {
            nextItems[existingIndex] = {
                  ...nextItems[existingIndex],
                  ...activity,
            };
      } else {
            nextItems.unshift(activity as TItem);
      }

      return {
            ...previous,
            items: nextItems,
      };
}

export function useBrandSearchActivity(brandId: string | null | undefined, options?: { enabled?: boolean }) {
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const [latestEvent, setLatestEvent] = useState<SearchExecutionActivity | null>(null);
      const enabled = Boolean(brandId) && (options?.enabled ?? true);
      const scopedLatestEvent = latestEvent?.brandId === brandId ? latestEvent : null;

      const activityQuery = useQuery(
            trpc.brands.getBrandSearchActivity.queryOptions({ brandId: brandId ?? "" }, { enabled }),
      );

      useEffect(() => {
            setLatestEvent((current) => (current?.brandId === brandId ? current : null));
      }, [brandId]);

      const setBrandActivityCache = (
            activity: BrandSearchActivity | null,
            searchActivity?: SearchExecutionActivity | null,
      ) => {
            if (!brandId) {
                  return;
            }

            queryClient.setQueryData(
                  trpc.brands.getBrandSearchActivity.queryKey({
                        brandId,
                  }),
                  activity,
            );

            if (searchActivity) {
                  queryClient.setQueryData(
                        trpc.searchSpecifications.getSearchSpecificationActivity.queryKey({
                              id: searchActivity.searchSpecificationId,
                        }),
                        searchActivity,
                  );
                  queryClient.setQueryData(
                        trpc.searchExecutions.getLatestSearchSpecificationSearchExecution.queryKey({
                              searchSpecificationId: searchActivity.searchSpecificationId,
                        }),
                        searchActivity,
                  );
                  queryClient.setQueriesData(
                        {
                              queryKey: trpc.searchExecutions.getSearchExecutions.queryKey({
                                    searchSpecificationId: searchActivity.searchSpecificationId,
                              }),
                        },
                        (previous) =>
                              upsertPaginatedSearchActivity(
                                    previous as { items: Array<SearchExecutionActivity> } | undefined,
                                    searchActivity,
                              ),
                  );
                  queryClient.setQueriesData(
                        {
                              queryKey: trpc.searchExecutions.getSearchExecutionsByBrandId.queryKey({
                                    brandId,
                              }),
                        },
                        (previous) =>
                              upsertPaginatedSearchActivity(
                                    previous as { items: Array<SearchExecutionActivity> } | undefined,
                                    searchActivity,
                              ),
                  );
            }
      };

      useSupabaseBroadcast({
            channelName: brandId ? `brand:${brandId}` : "",
            event: "search_activity_updated",
            callbackFn: (message: { payload?: SearchActivityRealtimePayload }) => {
                  if (!message.payload) {
                        return;
                  }

                  const searchActivity = searchActivityFromRealtimePayload(message.payload);
                  if (brandId && searchActivity.brandId !== brandId) {
                        return;
                  }

                  const brandActivity = brandActivityFromRealtimePayload(message.payload);
                  setLatestEvent(searchActivity);
                  setBrandActivityCache(brandActivity, searchActivity);
            },
      });

      return {
            activity: activityQuery.data ?? null,
            latestEvent: scopedLatestEvent,
            query: activityQuery,
            setOptimisticActivity: (
                  activity: BrandSearchActivity | null,
                  searchActivity?: SearchExecutionActivity | null,
            ) => {
                  setLatestEvent(searchActivity ?? null);
                  setBrandActivityCache(activity, searchActivity);
            },
      };
}
